// Loan record foundations — ADMIN-only add of an LS Setup catalog item,
// mirroring Deal Flow's <CriteriaCatalogCreateModal>. The Code is generated
// server-side ("LS-<next ordinal>"); the new active item appears on every
// already-generated checklist as Pending via lazy-create on the next read.
// Form polish: guided Owner-role multiselect, Tier radio group (Critical /
// Important / Low), and the structured due-date picker.

import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Badge, Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useCreateSetupCatalogItem } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  LOAN_SETUP_SECTIONS,
  type LoanSetupTier,
  type LoanSummaryDto,
} from "@/api/portfolioAdmin/types";
import { joinOwnerRoleCodes } from "./setupCatalogForm";
import { DueDateFormulaField, OwnerRolesField, TierField } from "./SetupCatalogFormFields";

interface SetupCatalogCreateModalProps {
  loan: LoanSummaryDto;
  open: boolean;
  onClose: () => void;
}

const TITLE_MAX = 200;

export function SetupCatalogCreateModal({
  loan,
  open,
  onClose,
}: SetupCatalogCreateModalProps) {
  const toast = useToast();
  const create = useCreateSetupCatalogItem(loan.id);

  const [title, setTitle] = useState("");
  const [tier, setTier] = useState<LoanSetupTier>("RedFlag");
  const [ownerRoles, setOwnerRoles] = useState<string[]>(["B1"]);
  const [section, setSection] = useState<string>(LOAN_SETUP_SECTIONS[0]);
  const [dueDateFormula, setDueDateFormula] = useState("Close + 30 days");
  const [description, setDescription] = useState("");

  // Reset to a blank form whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    setTitle("");
    setTier("RedFlag");
    setOwnerRoles(["B1"]);
    setSection(LOAN_SETUP_SECTIONS[0]);
    setDueDateFormula("Close + 30 days");
    setDescription("");
  }, [open]);

  const titleValid = title.trim().length > 0 && title.trim().length <= TITLE_MAX;
  const ownerValid = ownerRoles.length > 0;
  const formulaValid = dueDateFormula.trim().length > 0;
  const formValid = titleValid && ownerValid && formulaValid;

  const onSave = async () => {
    try {
      const created = await create.mutateAsync({
        title: title.trim(),
        tier,
        ownerRoleCode: joinOwnerRoleCodes(ownerRoles),
        section,
        dueDateFormula: dueDateFormula.trim(),
        description: description.trim() === "" ? null : description.trim(),
      });
      toast.success(`${created.code} added. Appears on all generated checklists.`);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Add setup item"
      description="Adds a catalog item that applies to every loan — already-generated checklists pick it up as Pending."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSave()}
            disabled={!formValid || create.isPending}
            data-testid="setup-catalog-create-save"
          >
            {create.isPending ? "Adding…" : "Add setup item"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4" data-testid="setup-catalog-create-modal">
        <div className="flex items-center gap-space-2">
          <Badge status="draft">LS-?</Badge>
          <span className="text-caption text-gray-mid">
            Code is assigned automatically.
          </span>
        </div>

        <FormField label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={TITLE_MAX}
            data-testid="setup-catalog-title"
          />
        </FormField>

        <TierField value={tier} onChange={setTier} />

        <FormField label="Section" required>
          <select
            className="px-space-2 py-space-1 border border-border-light rounded-md text-small bg-white-pure"
            value={section}
            onChange={(e) => setSection(e.target.value)}
            data-testid="setup-catalog-section"
          >
            {LOAN_SETUP_SECTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </FormField>

        <OwnerRolesField value={ownerRoles} onChange={setOwnerRoles} />

        <DueDateFormulaField
          value={dueDateFormula}
          onChange={setDueDateFormula}
          firstPaymentDate={loan.firstPaymentDate}
        />

        <FormField label="Description">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </FormField>
      </div>
    </Modal>
  );
}
